import { useCallback } from 'react';
import { Recipe } from '../../api/Recipe';
import useFavoriteToggleAction from '../favorites/useFavoriteToggleAction';
import {
  navigateAfterRecipeDeletion,
  refreshRecipeAfterUpdate,
  shareRecipePage,
  toggleRecipeFavoriteForPage,
} from '../../domains/recipes/use-cases/recipePageActions';
import {
  buildPublicRecipeUrl,
  buildShareRecipePayload,
  getFavoriteToggleErrorMessage,
} from './recipePageActions.helpers';

interface UseRecipePageActionsParams {
  recipe: Recipe | null;
  recipeId?: string;
  reloadRecipe: (errorPrefix?: string) => Promise<Recipe | null>;
  navigate: (path: string) => void;
  setEditing: (value: boolean) => void;
  isFavorite: (recipeId: string) => boolean;
  addFavorite: (recipe: Recipe) => Promise<void>;
  removeFavorite: (recipeId: string) => Promise<void>;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
}

interface UseRecipePageActionsResult {
  handleShare: () => Promise<void>;
  handleRecipeUpdatedInForm: () => Promise<void>;
  handleRecipeDeletedInForm: () => void;
  handleFavoriteToggle: () => Promise<void>;
}

const shareRecipe = async (recipe: Recipe, publicRecipeUrl: string): Promise<void> => {
  await navigator.share(buildShareRecipePayload(recipe, publicRecipeUrl));
};

const copyRecipeLink = async (publicRecipeUrl: string): Promise<void> => {
  await navigator.clipboard.writeText(publicRecipeUrl);
  alert('Lien de la recette publique copié dans le presse-papiers !');
};

export const useRecipePageActions = ({
  recipe,
  recipeId,
  reloadRecipe,
  navigate,
  setEditing,
  isFavorite,
  addFavorite,
  removeFavorite,
  showToast,
}: UseRecipePageActionsParams): UseRecipePageActionsResult => {
  const handleShare = useCallback(async () => shareRecipePage({
    recipe,
    buildPublicRecipeUrl,
    shareRecipe,
    copyRecipeLink,
    onShareError: (error) => {
      console.error('Erreur lors du partage:', error);
    },
    onCopyError: (error) => {
      console.error('Erreur lors de la copie du lien:', error);
    },
    supportsNativeShare: typeof navigator.share === 'function',
  }), [recipe]);

  const handleRecipeUpdatedInForm = useCallback(async () => refreshRecipeAfterUpdate({
    recipeId,
    reloadRecipe,
    navigateHome: () => navigate('/'),
    stopEditing: () => setEditing(false),
  }), [navigate, recipeId, reloadRecipe, setEditing]);

  const handleRecipeDeletedInForm = useCallback(() => {
    navigateAfterRecipeDeletion(() => navigate('/'));
  }, [navigate]);

  const toggleFavorite = useFavoriteToggleAction({
    isFavorite,
    addFavorite,
    removeFavorite,
    showToast,
  });

  const handleFavoriteToggle = useCallback(async () => toggleRecipeFavoriteForPage({
    recipe,
    toggleFavorite,
    showToast,
    getErrorMessage: getFavoriteToggleErrorMessage,
  }), [recipe, showToast, toggleFavorite]);

  return {
    handleShare,
    handleRecipeUpdatedInForm,
    handleRecipeDeletedInForm,
    handleFavoriteToggle,
  };
};

export default useRecipePageActions;
